import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

// Initialize Firebase Functions
const functions = getFunctions(getApp());

// AI Service for generating listing enhancements
class AIService {
  private generateAITitlesFn = httpsCallable(functions, 'generateAITitles');
  private generateAICategoriesFn = httpsCallable(functions, 'generateAICategories');
  private generateAIHighlightsFn = httpsCallable(functions, 'generateAIHighlights');
  private generateAISEOMetadataFn = httpsCallable(functions, 'generateAISEOMetadata');
  private generateAIBuyerTypesFn = httpsCallable(functions, 'generateAIBuyerTypes');
  private syncDDFListingsManualFn = httpsCallable(functions, 'syncDDFListingsManual');
  private resetDDFAITitleFlagsFn = httpsCallable(functions, 'resetDDFAITitleFlags');
  private runAIGenerationTitlesOnlyFn = httpsCallable(functions, 'runAIGenerationTitlesOnly');
  private generateAIForListingFn = httpsCallable(functions, 'generateAIForListing');

  /**
   * Generate AI titles for listings that don't have titles
   */
  async generateTitles(collectionName: string = 'ddfListings', skipProcessed: boolean = true): Promise<{
    success: boolean;
    processed: number;
    skipped?: number;
    results: Array<{ id: string; title?: string; error?: string; success: boolean; skipped?: boolean }>;
  }> {
    try {
      const result = await this.generateAITitlesFn({ collectionName, skipProcessed });
      return result.data as any;
    } catch (error: any) {
      console.error('Error generating AI titles:', error);
      throw new Error(error.message || 'Failed to generate AI titles');
    }
  }

  /**
   * Generate AI categories for listings that don't have categories
   */
  async generateCategories(collectionName: string = 'ddfListings', skipProcessed: boolean = true): Promise<{
    success: boolean;
    processed: number;
    skipped?: number;
    results: Array<{ id: string; category?: string; subcategory?: string; error?: string; success: boolean; skipped?: boolean }>;
  }> {
    try {
      const result = await this.generateAICategoriesFn({ collectionName, skipProcessed });
      return result.data as any;
    } catch (error: any) {
      console.error('Error generating AI categories:', error);
      throw new Error(error.message || 'Failed to generate AI categories');
    }
  }

  /**
   * Generate AI highlights for listings that don't have highlights
   */
  async generateHighlights(collectionName: string = 'ddfListings', skipProcessed: boolean = true): Promise<{
    success: boolean;
    processed: number;
    skipped?: number;
    results: Array<{ id: string; highlightsCount?: number; error?: string; success: boolean; skipped?: boolean }>;
  }> {
    try {
      const result = await this.generateAIHighlightsFn({ collectionName, skipProcessed });
      return result.data as any;
    } catch (error: any) {
      console.error('Error generating AI highlights:', error);
      throw new Error(error.message || 'Failed to generate AI highlights');
    }
  }

  /**
   * Generate SEO title and URL for listings
   */
  async generateSEOMetadata(collectionName: string = 'ddfListings', skipProcessed: boolean = true): Promise<{
    success: boolean;
    processed: number;
    skipped?: number;
    results: Array<{ id: string; seoTitle?: string; seoUrl?: string; error?: string; success: boolean; skipped?: boolean }>;
  }> {
    try {
      const result = await this.generateAISEOMetadataFn({ collectionName, skipProcessed });
      return result.data as any;
    } catch (error: any) {
      console.error('Error generating SEO metadata:', error);
      throw new Error(error.message || 'Failed to generate SEO metadata');
    }
  }

  /**
   * Identify buyer types for listings
   */
  async generateBuyerTypes(collectionName: string = 'ddfListings', skipProcessed: boolean = true): Promise<{
    success: boolean;
    processed: number;
    skipped?: number;
    results: Array<{ id: string; buyerTypesCount?: number; buyerTypes?: string[]; error?: string; success: boolean; skipped?: boolean }>;
  }> {
    try {
      const result = await this.generateAIBuyerTypesFn({ collectionName, skipProcessed });
      return result.data as any;
    } catch (error: any) {
      console.error('Error generating buyer types:', error);
      throw new Error(error.message || 'Failed to generate buyer types');
    }
  }

  /**
   * Manually sync DDF listings
   */
  async syncDDFListings(): Promise<{
    success: boolean;
    stats: {
      new: number;
      updated: number;
      unchanged: number;
      deactivated: number;
      errors: number;
    };
    message: string;
  }> {
    try {
      const result = await this.syncDDFListingsManualFn({});
      return result.data as any;
    } catch (error: any) {
      console.error('Error syncing DDF listings:', error);
      throw new Error(error.message || 'Failed to sync DDF listings');
    }
  }

  /**
   * Run AI title generation in batches until all unprocessed ddfListings have titles (up to 1000 per run).
   */
  async runAIGenerationTitlesOnly(): Promise<{
    success: boolean;
    processed: number;
    skipped?: number;
    batches: number;
    results: Array<{ id: string; title?: string; error?: string; success: boolean; skipped?: boolean }>;
    message: string;
  }> {
    try {
      const result = await this.runAIGenerationTitlesOnlyFn({});
      return result.data as any;
    } catch (error: any) {
      console.error('Error running AI titles only:', error);
      throw new Error(error.message || 'Failed to run AI titles');
    }
  }

  /**
   * ONE-OFF: Reset AI title processed flags for all ddfListings
   */
  async resetDDFAITitleFlags(): Promise<{
    success: boolean;
    updated: number;
  }> {
    try {
      const result = await this.resetDDFAITitleFlagsFn({});
      return result.data as any;
    } catch (error: any) {
      console.error('Error resetting DDF AI title flags:', error);
      throw new Error(error.message || 'Failed to reset DDF AI title flags');
    }
  }

  /**
   * Generate AI content for a specific listing and field
   * type can be: 'title', 'categories', 'highlights', 'metadata', 'buyerTypes'
   */
  async generateForListing(params: {
    collectionName: string;
    listingId: string;
    type: 'title' | 'categories' | 'highlights' | 'metadata' | 'buyerTypes';
  }): Promise<{
    success: boolean;
    type: string;
    listingId: string;
    result: any;
  }> {
    try {
      const result = await this.generateAIForListingFn(params);
      return result.data as any;
    } catch (error: any) {
      console.error('Error generating AI for listing:', error);
      throw new Error(error.message || 'Failed to generate AI content for listing');
    }
  }
}

export const aiService = new AIService();
export default aiService;
